/**
 * Asset library: loads public/assets (index.json + bundle manifests + images) through the
 * Platform and instantiates linkage classes like the original sub-SWF MainTimeline.get_object().
 */
import { parseBitmapPack, packEntry, type BitmapPack } from '../../assets/bitmap-pack';
import type {
  AssetBundleManifest,
  AssetIndex,
  BitmapDef,
  ButtonDef,
  CharacterDef,
  FontDef,
  ImageRef,
  SoundDef,
} from '../../assets/manifest';
import type { DecodedImage, Platform } from '../platform/types';
import type { SymbolContext } from './types';
import { BitmapData, premultiply } from '../display/BitmapData';
import { Bitmap } from '../display/Bitmap';
import { MovieClip } from '../display/MovieClip';
import { Sprite } from '../display/Sprite';
import { SimpleButton } from '../display/SimpleButton';
import { MorphShape, Shape } from '../display/Shape';
import type { DisplayObject } from '../display/DisplayObject';
import { TextField } from '../text/TextField';
import { Sound } from '../media/Sound';
import { filterFromDef } from '../filters/filters';
import { constructObject } from '../player/lifecycle';
import { TWIPS } from '../utils/numeric';
import { rt } from '../player/runtime';

export interface AssetLoadOptions {
  /** 'lazy' (default): map bundles are loaded by `loadMap()`; 'all': everything at start. */
  maps?: 'all' | 'lazy';
  /** Decode timeline shape/morph images: 'eager' (default) at load, 'lazy' via preloadImages(), 'none'. */
  shapes?: 'eager' | 'lazy' | 'none';
  /** Load only these core bundles (default: all). */
  bundles?: string[];
  /** Parallel image decodes (default 8). */
  concurrency?: number;
  /**
   * Bundles whose shape/morph images `loadMap()` also decodes before it resolves (browser host: ['obj']).
   * With shapes: 'lazy' a timeline shape is fetched on its first draw, so an animation shown for the first time in a
   * session renders empty until its atlas page arrives - the worm crosshair and the launch-power bar (711 morph
   * samples spread over 8 atlas pages) were missing for the whole first aim. A failed preload never fails loadMap.
   */
  preloadWithMaps?: string[];
}

export function joinUrl(base: string, path: string): string {
  if (!base) return path;
  if (/^[a-z]+:\/\//i.test(path) || path.startsWith('/')) return path;
  return base.replace(/\/+$/, '') + '/' + path.replace(/^\.\//, '');
}

function dirname(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

async function pool<T>(items: T[], limit: number, fn: (t: T) => Promise<void>): Promise<void> {
  let i = 0;
  const workers: Promise<void>[] = [];
  for (let w = 0; w < Math.max(1, limit); w++) {
    workers.push(
      (async () => {
        while (i < items.length) {
          const item = items[i++];
          await fn(item);
        }
      })(),
    );
  }
  await Promise.all(workers);
}

/** Decoded image cache keyed by resolved URL. */
export class ImageStore {
  private readonly images = new Map<string, DecodedImage>();
  private readonly pending = new Map<string, Promise<DecodedImage>>();
  /** Image URL -> [pack URL, entry name] for images shipped inside a lazily loaded pack (HD variants). */
  private readonly packed = new Map<string, [string, string]>();
  private readonly packs = new Map<string, Promise<BitmapPack>>();
  constructor(private readonly platform: Platform) {}

  /** Declares that `url` is entry `rel` of the pack at `packUrl` (fetched once, on the first load of any entry). */
  registerPacked(url: string, packUrl: string, rel: string): void {
    this.packed.set(url, [packUrl, rel]);
  }

  private async readBytes(url: string): Promise<Uint8Array> {
    const inPack = this.packed.get(url);
    if (!inPack) return this.platform.readBinary(url);
    let p = this.packs.get(inPack[0]);
    if (!p) this.packs.set(inPack[0], (p = this.platform.readBinary(inPack[0]).then(parseBitmapPack)));
    const bytes = packEntry(await p, inPack[1]);
    if (!bytes) throw new Error(`${inPack[1]} missing in ${inPack[0]}`);
    return bytes;
  }

  peek(url: string): DecodedImage | undefined {
    return this.images.get(url);
  }
  has(url: string): boolean {
    return this.images.has(url);
  }
  set(url: string, img: DecodedImage): void {
    this.images.set(url, img);
  }
  load(url: string): Promise<DecodedImage> {
    const done = this.images.get(url);
    if (done) return Promise.resolve(done);
    let p = this.pending.get(url);
    if (!p) {
      p = (async () => {
        try {
          const bytes = await this.readBytes(url);
          const img = await this.platform.decodeImage(bytes, url);
          this.images.set(url, img);
          return img;
        } finally {
          this.pending.delete(url); // a failed load must not keep pendingCount above 0 forever
        }
      })();
      this.pending.set(url, p);
    }
    return p;
  }
  /** Number of image loads still in flight (hosts/tests wait for 0 before judging a rendered frame). */
  get pendingCount(): number {
    return this.pending.size;
  }
  get size(): number {
    return this.images.size;
  }
}

function baseName(b: string | undefined): string {
  if (!b) return '';
  const parts = b.split(/[.:]+/);
  return parts[parts.length - 1];
}

/** Per-bundle symbol context (see SymbolContext). */
export class BundleContext implements SymbolContext {
  readonly bundle: string;
  readonly swfVersion: number;
  readonly dir: string;
  readonly classIdToName = new Map<number, string>();
  loaded = false;

  constructor(
    readonly library: AssetLibrary,
    readonly manifest: AssetBundleManifest,
    dir: string,
  ) {
    this.bundle = manifest.name;
    this.swfVersion = manifest.swfVersion || 10;
    this.dir = dir;
    for (const [name, id] of Object.entries(manifest.classes ?? {})) if (!this.classIdToName.has(id)) this.classIdToName.set(id, name);
  }

  resolve(src: string): string {
    // (asked for every shape drawn, every frame: joined once per image)
    let u = this.resolved.get(src);
    if (u === undefined) this.resolved.set(src, (u = joinUrl(this.dir, src)));
    return u;
  }
  private readonly resolved = new Map<string, string>();
  image(src: string): DecodedImage | undefined {
    return this.library.images.peek(this.resolve(src));
  }
  character(id: number): CharacterDef | undefined {
    // A variant of the art for the host's variant (Runtime.assetVariant: the UI language), when the bundle has one.
    const variant = rt().assetVariant;
    const alt = variant ? this.manifest.localized?.[String(id)]?.[variant] : undefined;
    return this.manifest.characters[String(alt ?? id)];
  }
  instantiate(id: number, className?: string): any {
    return this.library.$instantiateCharacter(this, id, className);
  }
}

/**
 * Equivalent of a loaded sub-SWF's MainTimeline: `get_object(name)` instantiates linkage classes,
 * unknown names give `{}` (original behaviour); `get(name)` is the FWP bundle's GenericAsset.get,
 * which throws ArgumentError for unknown ids.
 */
export class AssetContainer {
  /** @internal */ $ctx: BundleContext;
  constructor(ctx?: BundleContext) {
    this.$ctx = ctx as BundleContext;
  }
  get name(): string {
    return this.$ctx.bundle;
  }
  get sourceClass(): string {
    return this.$ctx.manifest.sourceClass;
  }
  has(className: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.$ctx.manifest.classes ?? {}, className);
  }
  get_object(className: string): any {
    const m = this.$ctx.manifest;
    const comp = m.composites?.[className];
    if (comp) {
      const make = (it: any): any => ('class' in it ? this.$ctx.library.$instantiateClass(this.$ctx, it.class) : it.value);
      if (comp.kind === 'array') return comp.items.map(make);
      const o: Record<string, any> = {};
      for (const k of Object.keys(comp.entries)) o[k] = make(comp.entries[k]);
      return o;
    }
    if (!this.has(className)) return m.getObjectFallback === 'undefined' ? undefined : {};
    return this.$ctx.library.$instantiateClass(this.$ctx, className);
  }
  get(className: string, convertToBitmap: boolean = false): any {
    if (!this.has(className)) {
      const e = new Error(`ArgumentError: Failed to get Object in [object GenericAsset], id: ${className}`);
      e.name = 'ArgumentError';
      throw e;
    }
    const o = this.$ctx.library.$instantiateClass(this.$ctx, className);
    if (convertToBitmap && o instanceof BitmapData) return new Bitmap(o);
    return o;
  }
  /** Linkage class names of this bundle. */
  classNames(): string[] {
    return Object.keys(this.$ctx.manifest.classes ?? {});
  }
}

export interface FontInfo {
  bundle: string;
  name: string;
  bold: boolean;
  italic: boolean;
  cssFamily: string;
  url: string | null;
  /** A subset of its face (FontDef.partial): the system font of that name goes first. */
  partial?: boolean;
}

export class AssetLibrary {
  readonly platform: Platform;
  readonly baseUrl: string;
  readonly index: AssetIndex;
  readonly images: ImageStore;
  readonly fonts: FontInfo[] = [];
  /** @internal */ readonly $contexts = new Map<string, BundleContext>();
  /** @internal */ readonly $containers = new Map<string, AssetContainer>();
  /** @internal decoded BitmapDef pixels (premultiplied) by URL */ readonly $pixels = new Map<string, { w: number; h: number; data: Uint32Array }>();
  private readonly opts: Required<Pick<AssetLoadOptions, 'maps' | 'shapes' | 'concurrency' | 'preloadWithMaps'>>;

  /** Prefer `AssetLibrary.load()`; `new AssetLibrary()` gives an empty library. */
  constructor(platform?: Platform, baseUrl: string = '', index?: AssetIndex, opts: AssetLoadOptions = {}) {
    this.platform = (platform ?? rt().platform) as Platform;
    this.baseUrl = baseUrl;
    this.index = index ?? { version: 1, generatedFrom: { exeSha256: '', swfSha256: '' }, bundles: [], maps: {} };
    this.images = new ImageStore(this.platform);
    this.opts = { maps: opts.maps ?? 'lazy', shapes: opts.shapes ?? 'eager', concurrency: opts.concurrency ?? 8, preloadWithMaps: opts.preloadWithMaps ?? [] };
  }

  /** Load index + bundle manifests + decode bitmaps (and shape images unless shapes != 'eager'). */
  static async load(platform: Platform, baseUrl: string, opts: AssetLoadOptions = {}): Promise<AssetLibrary> {
    const index = JSON.parse(await platform.readText(joinUrl(baseUrl, 'index.json'))) as AssetIndex;
    const lib = new AssetLibrary(platform, baseUrl, index, opts);
    const wanted = index.bundles.filter((b) => (b.kind === 'core' ? !opts.bundles || opts.bundles.includes(b.name) : lib.opts.maps === 'all'));
    await Promise.all(wanted.map((b) => lib.loadBundle(b.name)));
    return lib;
  }

  /** Construct from in-memory manifests (tests / tools); images still load through the platform. */
  static async fromManifests(platform: Platform, baseUrl: string, index: AssetIndex, manifests: Record<string, AssetBundleManifest>, opts: AssetLoadOptions = {}): Promise<AssetLibrary> {
    const lib = new AssetLibrary(platform, baseUrl, index, opts);
    for (const b of index.bundles) {
      const m = manifests[b.name];
      if (!m) continue;
      if (b.kind === 'map' && lib.opts.maps !== 'all') {
        lib.registerBundle(m, dirname(b.manifest));
        continue;
      }
      await lib.registerAndDecode(m, dirname(b.manifest));
    }
    return lib;
  }

  private registerBundle(m: AssetBundleManifest, relDir: string): BundleContext {
    const ctx = new BundleContext(this, m, joinUrl(this.baseUrl, relDir));
    this.$contexts.set(m.name, ctx);
    this.$containers.set(m.name, new AssetContainer(ctx));
    if (m.hdPack) {
      const packUrl = ctx.resolve(m.hdPack);
      for (const def of Object.values(m.characters)) {
        if (def.type === 'shape' && def.image && def.image.hd) this.images.registerPacked(ctx.resolve(def.image.hd.src), packUrl, def.image.hd.src);
      }
    }
    return ctx;
  }

  private async registerAndDecode(m: AssetBundleManifest, relDir: string): Promise<BundleContext> {
    let ctx = this.$contexts.get(m.name);
    if (!ctx) ctx = this.registerBundle(m, relDir);
    if (ctx.loaded) return ctx;
    const bitmapUrls: string[] = [];
    const imageUrls = new Set<string>();
    for (const def of Object.values(m.characters)) {
      if (def.type === 'bitmap') bitmapUrls.push(ctx.resolve(def.src));
      else if (def.type === 'shape') {
        if (def.image) imageUrls.add(ctx.resolve(def.image.src));
      } else if (def.type === 'morph') {
        for (const s of def.samples) if (s.image) imageUrls.add(ctx.resolve(s.image.src));
      } else if (def.type === 'font') {
        this.fonts.push({ bundle: m.name, name: def.name, bold: def.bold, italic: def.italic, cssFamily: def.cssFamily, url: def.file ? ctx.resolve(def.file) : null, ...(def.partial ? { partial: true } : {}) });
      }
    }
    // One request for all bitmaps of the bundle when a pack exists (identical PNG bytes; see bitmap-pack.ts).
    let pack: BitmapPack | null = null;
    if (m.bitmapPack && bitmapUrls.length > 1) {
      try {
        pack = parseBitmapPack(await this.platform.readBinary(ctx.resolve(m.bitmapPack)));
      } catch {
        pack = null; // fall back to individual files
      }
    }
    const relOf = new Map<string, string>();
    if (pack) for (const def of Object.values(m.characters)) if (def.type === 'bitmap') relOf.set(ctx.resolve(def.src), def.src);
    await pool(bitmapUrls, this.opts.concurrency, async (url) => {
      if (this.$pixels.has(url)) return;
      const packed = pack ? packEntry(pack, relOf.get(url) ?? '') : null;
      const bytes = packed ?? (await this.platform.readBinary(url));
      const img = await this.platform.decodeImage(bytes, url);
      this.$pixels.set(url, { w: img.width, h: img.height, data: rgbaToPremultiplied(img.data, img.width * img.height) });
      // Shapes that draw these bitmaps (ImageRef src under bitmaps/) then need no extra request.
      if (img.source || pack) this.images.set(url, img);
    });
    if (this.opts.shapes === 'eager') await pool([...imageUrls], this.opts.concurrency, async (url) => void (await this.images.load(url)));
    ctx.loaded = true;
    return ctx;
  }

  private async loadBundle(name: string): Promise<BundleContext> {
    const entry = this.index.bundles.find((b) => b.name === name);
    if (!entry) throw new Error(`Unknown asset bundle "${name}"`);
    const existing = this.$contexts.get(name);
    if (existing?.loaded) return existing;
    const m = existing?.manifest ?? (JSON.parse(await this.platform.readText(joinUrl(this.baseUrl, entry.manifest))) as AssetBundleManifest);
    return this.registerAndDecode(m, dirname(entry.manifest));
  }

  /** Decode all shape/morph images of loaded bundles (for shapes: 'lazy'). */
  async preloadImages(bundles?: string[]): Promise<void> {
    const urls = new Set<string>();
    for (const ctx of this.$contexts.values()) {
      if (bundles && !bundles.includes(ctx.bundle)) continue;
      for (const def of Object.values(ctx.manifest.characters)) {
        if (def.type === 'shape') {
          if (def.image) urls.add(ctx.resolve(def.image.src));
        } else if (def.type === 'morph') {
          for (const s of def.samples) if (s.image) urls.add(ctx.resolve(s.image.src));
        }
      }
    }
    await pool([...urls], this.opts.concurrency, async (url) => void (await this.images.load(url)));
  }

  /**
   * Renderer helper: embedded font for a Flash font name + style (exact style match first, then any
   * style of that name). Returns null when the font is not embedded (use a system fallback).
   */
  resolveFont(name: string, bold: boolean, italic: boolean): FontInfo | null {
    let any: FontInfo | null = null;
    for (const f of this.fonts) {
      if (f.name !== name) continue;
      if (f.bold === bold && f.italic === italic) return f;
      any = any ?? f;
    }
    return any;
  }

  container(bundle: string): AssetContainer {
    const c = this.$containers.get(bundle);
    if (!c) throw new Error(`Asset bundle "${bundle}" is not loaded`);
    return c;
  }

  hasContainer(bundle: string): boolean {
    return this.$containers.has(bundle);
  }

  /** Container by the original main-SWF linkage class (e.g. "FWP_ASSET_OBJ"). */
  containerBySourceClass(sourceClass: string): AssetContainer | null {
    for (const c of this.$containers.values()) if (c.sourceClass === sourceClass) return c;
    return null;
  }

  hasMap(mapId: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.index.maps ?? {}, mapId);
  }

  /** Load a map bundle (needed before getMapBitmap in 'lazy' mode). */
  /** Loads a map bundle (and, per `preloadWithMaps`, decodes the battle bundles' shape images first used in a battle). */
  async loadMap(mapId: string): Promise<void> {
    const bundle = this.index.maps?.[mapId];
    if (!bundle) throw new Error(`Unknown map "${mapId}"`);
    const warm = this.opts.preloadWithMaps.length ? this.preloadImages(this.opts.preloadWithMaps).catch(() => undefined) : undefined;
    await Promise.all([this.loadBundle(bundle), warm]);
  }

  isMapLoaded(mapId: string): boolean {
    const bundle = this.index.maps?.[mapId];
    return !!bundle && !!this.$contexts.get(bundle)?.loaded;
  }

  /** Terrain bitmap of a map (fresh copy; the map bundle's "dmap" BitmapData class). */
  getMapBitmap(mapId: string): BitmapData {
    const bundle = this.index.maps?.[mapId];
    if (!bundle) throw new Error(`Unknown map "${mapId}"`);
    const ctx = this.$contexts.get(bundle);
    if (!ctx || !ctx.loaded) throw new Error(`Map "${mapId}" (bundle ${bundle}) is not loaded: call AssetLibrary.loadMap() first`);
    const cls = ctx.manifest.classes['dmap'] !== undefined ? 'dmap' : Object.keys(ctx.manifest.classes).find((k) => ctx.character(ctx.manifest.classes[k])?.type === 'bitmap');
    if (!cls) throw new Error(`Map bundle ${bundle} has no bitmap class`);
    return this.$instantiateClass(ctx, cls) as BitmapData;
  }

  // ------------------------------------------------------------------ instantiation
  /** @internal Fresh BitmapData for a BitmapDef. */
  $bitmapData(ctx: BundleContext, def: BitmapDef): BitmapData {
    const url = ctx.resolve(def.src);
    const px = this.$pixels.get(url);
    if (!px) {
      // BLOCKED/UNVERIFIED fallback: image not decoded (missing file) -> transparent bitmap of the declared size.
      return BitmapData.$fromPremultiplied(Math.max(1, def.width), Math.max(1, def.height), true, new Uint32Array(Math.max(1, def.width) * Math.max(1, def.height)));
    }
    return BitmapData.$fromPremultiplied(px.w, px.h, true, px.data.slice());
  }

  /** @internal Instantiate a linkage class (script `new ClassName()`). */
  $instantiateClass(ctx: BundleContext, className: string): any {
    const id = ctx.manifest.classes[className];
    const def = ctx.character(id);
    const base = baseName(ctx.manifest.classBase?.[className]);
    if (!def) return {};
    if (def.type === 'bitmap' || base === 'BitmapData') {
      if (def.type !== 'bitmap') return {};
      const bd = this.$bitmapData(ctx, def);
      (bd as any).$className = className;
      return bd;
    }
    if (def.type === 'sound' || base === 'Sound') {
      if (def.type !== 'sound') return {};
      return this.$sound(ctx, def, className);
    }
    const obj = this.$instantiateCharacter(ctx, id, className);
    if (!obj) return {};
    if (def.type === 'sprite') {
      if (base === 'Sprite') obj.stop();
      // AS3 constructors construct the first frame's timeline children immediately.
      for (const k of (obj as DisplayObject).$lifecycleChildren() ?? []) if (!k.$constructed) constructObject(k);
    }
    return obj;
  }

  /** @internal */ $sound(ctx: BundleContext, def: SoundDef, className: string): Sound {
    return Sound.$create(ctx.resolve(def.src), def.durationMs, className);
  }

  /** @internal Timeline/character factory. */
  $instantiateCharacter(ctx: BundleContext, id: number, className?: string): any {
    const def = ctx.character(id);
    if (!def) return null;
    let obj: any = null;
    switch (def.type) {
      case 'shape': {
        const s = new Shape();
        s.$sym = ctx;
        s.$def = def;
        obj = s;
        break;
      }
      case 'morph': {
        const m = new MorphShape();
        m.$sym = ctx;
        m.$def = def;
        obj = m;
        break;
      }
      case 'bitmap': {
        const b = new Bitmap(this.$bitmapData(ctx, def));
        b.$sym = ctx;
        obj = b;
        break;
      }
      case 'sprite': {
        const mc = new MovieClip();
        mc.$className = className ?? ctx.classIdToName.get(id) ?? '';
        mc.$initTimeline(ctx, def);
        obj = mc;
        break;
      }
      case 'button':
        obj = this.$buildButton(ctx, def);
        break;
      case 'edittext': {
        const tf = new TextField();
        tf.$sym = ctx;
        const fd = def.fontId !== undefined ? ctx.character(def.fontId) : undefined;
        tf.$initFromDef(def, fd && fd.type === 'font' ? { name: fd.name, bold: fd.bold, italic: fd.italic } : undefined);
        obj = tf;
        break;
      }
      default:
        return null;
    }
    obj.$charId = id;
    return obj;
  }

  /** @internal Build a SimpleButton from a ButtonDef (multi-record states become Sprites). */
  $buildButton(ctx: BundleContext, def: ButtonDef): SimpleButton {
    const btn = new SimpleButton();
    btn.$sym = ctx;
    btn.$trackAsMenu = !!def.trackAsMenu;
    const build = (state: 'up' | 'over' | 'down' | 'hit'): DisplayObject | null => {
      const recs = def.records.filter((r) => r.states[state]).sort((a, b) => a.depth - b.depth);
      if (!recs.length) return null;
      const objs: DisplayObject[] = [];
      for (const r of recs) {
        const o: DisplayObject | null = this.$instantiateCharacter(ctx, r.charId);
        if (!o) continue;
        o.$byTimeline = true;
        o.$depth = r.depth;
        if (r.matrix) {
          const [a, b, c, d, tx, ty] = r.matrix;
          o.$setMatrix({ a, b, c, d, tx: Math.round(tx * TWIPS), ty: Math.round(ty * TWIPS) });
        }
        o.$applyPlace({
          colorTransform: r.colorTransform,
          filters: r.filters?.map(filterFromDef),
          blendMode: r.blendMode,
        });
        objs.push(o);
      }
      if (!objs.length) return null;
      if (objs.length === 1) return objs[0];
      const sp = new Sprite();
      sp.$sym = ctx;
      for (const o of objs) sp.$timelineInsert(o, o.$depth);
      return sp;
    };
    btn.upState = build('up');
    btn.overState = build('over');
    btn.downState = build('down');
    btn.hitTestState = build('hit');
    return btn;
  }
}

function rgbaToPremultiplied(rgba: Uint8ClampedArray | Uint8Array, n: number): Uint32Array {
  const out = new Uint32Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) {
    out[i] = premultiply(((rgba[j + 3] << 24) | (rgba[j] << 16) | (rgba[j + 1] << 8) | rgba[j + 2]) >>> 0, true);
  }
  return out;
}

export type { ImageRef };
